import { useState, useEffect } from 'react'
import { X, Lock, AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react'
import { password as passwordApi } from '../api'
import { useToastStore } from '../stores/toast'

interface PasswordChangeDialogProps {
  onClose: () => void
  onSuccess?: () => void
  /** 画面に表示する注意書き（期限切れ時など） */
  reason?: string
}

// パスワードポリシー: 12文字以上、大小英字・数字・記号
const POLICY_TEXT = '12文字以上、大文字・小文字・数字・記号をすべて含めてください'

function checkPolicy(pw: string): { ok: boolean; reasons: string[] } {
  const reasons: string[] = []
  if (pw.length < 12) reasons.push('12文字以上必要です')
  if (!/[a-z]/.test(pw)) reasons.push('小文字を含めてください')
  if (!/[A-Z]/.test(pw)) reasons.push('大文字を含めてください')
  if (!/[0-9]/.test(pw)) reasons.push('数字を含めてください')
  if (!/[^a-zA-Z0-9]/.test(pw)) reasons.push('記号を含めてください')
  return { ok: reasons.length === 0, reasons }
}

export default function PasswordChangeDialog({ onClose, onSuccess, reason }: PasswordChangeDialogProps) {
  const toast = useToastStore()
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !saving) onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose, saving])

  const policy = checkPolicy(newPassword)
  const matched = newPassword.length > 0 && newPassword === confirmPassword
  const canSubmit = !!currentPassword && policy.ok && matched && !saving

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!canSubmit) return
    if (currentPassword === newPassword) {
      setError('現在と同じパスワードは使用できません')
      return
    }

    setSaving(true)
    try {
      await passwordApi.change(currentPassword, newPassword)
      toast.success('パスワードを変更しました')
      onSuccess?.()
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'パスワード変更に失敗しました')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40"
      onClick={(e) => {
        if (e.target === e.currentTarget && !saving) onClose()
      }}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
            <Lock className="w-5 h-5 text-blue-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-gray-900">パスワードを変更</h3>
            <p className="text-sm text-gray-600 mt-1">{POLICY_TEXT}</p>
          </div>
          <button
            onClick={() => !saving && onClose()}
            className="text-gray-400 hover:text-gray-600 flex-shrink-0 disabled:opacity-50"
            disabled={saving}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {reason && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm mb-4">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            {reason}
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 text-red-700 text-sm mb-4">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">現在のパスワード</label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
              autoComplete="current-password"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">新しいパスワード</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
              autoComplete="new-password"
              required
            />
            {newPassword.length > 0 && (
              <ul className="mt-2 space-y-0.5">
                {(
                  [
                    { label: '12文字以上', ok: newPassword.length >= 12 },
                    { label: '大文字を含む', ok: /[A-Z]/.test(newPassword) },
                    { label: '小文字を含む', ok: /[a-z]/.test(newPassword) },
                    { label: '数字を含む', ok: /[0-9]/.test(newPassword) },
                    { label: '記号を含む', ok: /[^a-zA-Z0-9]/.test(newPassword) },
                  ] as const
                ).map((item) => (
                  <li
                    key={item.label}
                    className={`flex items-center gap-1.5 text-xs ${
                      item.ok ? 'text-green-600' : 'text-gray-400'
                    }`}
                  >
                    <CheckCircle2 className="w-3 h-3" />
                    {item.label}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">新しいパスワード（確認）</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
              autoComplete="new-password"
              required
            />
            {confirmPassword.length > 0 && !matched && (
              <p className="text-xs text-red-600 mt-1">パスワードが一致しません</p>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition disabled:opacity-50"
            >
              キャンセル
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="px-4 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 rounded-lg transition flex items-center gap-2"
            >
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {saving ? '変更中...' : '変更'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
